var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { MainWindowManager } from '../UtilsClasses/MainWindowManager';
import { MathUtils } from '@shared/utils/MathUtils';
import SnapshotScreenTemplate from '@front/templates/SnapshotScreenTemplate.html';
import { TerceraWindowBase } from './TerceraWindowBase';
import { DateTimeUtils } from '@shared/utils/Time/DateTimeUtils';
import { Account } from '@shared/commons/cache/Account';
import { DataCache } from '@shared/commons/DataCache';
import { InstrumentUtils } from '@shared/utils/Instruments/InstrumentUtils';
import { ScreensNames } from '../UtilsClasses/FactoryConstants';
import { WDSettingsUtils } from '../UtilsClasses/WDGeneralSettingsUtils';
import { WDSettings } from '../settings/WDGeneralSettings';
import { ResourcesManager } from '@shared/commons/properties/ResourcesManager';
import { StringUtils } from '@shared/utils/StringUtils';
let instance = null;
export class SnapshotScreen extends TerceraWindowBase {
    get message() {
        return this.get('message');
    }
    set message(value) {
        void this.set('message', value);
        this.updateHeader();
    }
    constructor(initProps = null, data) {
        super(initProps);
        this.instrument = null;
        this.needCenterLocation = false;
        if (data != null) {
            this.instrument = data.instrument;
            this.needCenterLocation = data.needCenterLocation;
        }
    }
    getType() {
        return ScreensNames.SnapshotScreen;
    }
    oninit() {
        super.oninit();
        ResourcesManager.onLocaleChange.Subscribe(this.localize, this);
        void this.set('displayAmountInLots', WDSettingsUtils.displayAmountInLots());
        WDSettings.settingsChanged.Subscribe(this.settingsChanged, this);
        Account.TradeStatusChanged.Subscribe(this.updateSnapshotBtn, this);
    }
    oncomplete() {
        super.oncomplete();
        this.on('getSnapshotBtnClick', this.getSnapshotBtnClickAsync.bind(this));
        this.on('closeBtnClick', this.closeBtnClick.bind(this));
        this.IncreaseWidthIfNeed();
        this.localize();
        if (this.needCenterLocation) {
            this.center();
        }
    }
    dispose() {
        ResourcesManager.onLocaleChange.UnSubscribe(this.localize, this);
        WDSettings.settingsChanged.UnSubscribe(this.settingsChanged, this);
        Account.TradeStatusChanged.UnSubscribe(this.updateSnapshotBtn, this);
        super.dispose();
    }
    localize() {
        this.updateSnapshotBtn();
        this.updateHeader();
        void this.set({
            closeBtnText: Resources.getResource('screen.snapshot.closeBtnText'),
            lastLabelText: Resources.getResource('screen.snapshot.lastLabel'),
            bidLabelText: Resources.getResource('screen.snapshot.bidLabel'),
            askLabelText: Resources.getResource('screen.snapshot.askLabel'),
            priceLabelText: Resources.getResource('screen.snapshot.priceLabel'),
            sizeLabelText: Resources.getResource('screen.snapshot.sizeLabel'),
            changeLabelText: Resources.getResource('screen.snapshot.changeLabel'),
            highLabelText: Resources.getResource('screen.snapshot.highLabel'),
            lowLabelText: Resources.getResource('screen.snapshot.lowLabel'),
            changePercentLabelText: Resources.getResource('screen.snapshot.changePercentLabel'),
            volumeLabelText: Resources.getResource('screen.snapshot.volumeLabel')
        });
    }
    getSnapshotBtnClickAsync() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.sendSnapshotRequestMessageAsync();
            if (!isNullOrUndefined(response)) {
                this.message = response;
            }
            this.updateSnapshotBtn();
        });
    }
    closeBtnClick() {
        this.close();
    }
    updateHeader() {
        const header = Resources.getResource('screen.snapshot.header');
        const additionalHeader = !isNullOrUndefined(this.message)
            ? this.getColoredTimeForHeader(this.message.ReceivedAt)
            : this.get('strNA');
        void this.set({
            header: header + ' ' + additionalHeader
        });
    }
    updateSnapshotBtn() {
        let text = Resources.getResource('screen.snapshot.getSnapshotBtnText');
        let tt = '';
        const availableNum = DataCache.getAvailableNumberOfSnapshotRequests();
        if (availableNum >= 0) {
            text += ' (' + availableNum + ')';
            tt = Resources.getResource('screen.snapshot.getSnapshotBtnText.' + (availableNum > 0 ? 'moreZero' : 'Zero') + '.tt');
        }
        else {
            tt = Resources.getResource('screen.snapshot.getSnapshotBtnText.minusOne.tt');
        }
        void this.set({
            getSnapshotBtnText: text,
            getSnapshotBtnTooltip: tt,
            getSnapshotBtnDisabled: availableNum === 0
        });
    }
    IncreaseWidthIfNeed() { }
    settingsChanged() {
        const newSettingsInLots = WDSettingsUtils.displayAmountInLots();
        if (this.get('displayAmountInLots') === newSettingsInLots) {
            return;
        }
        void this.set('displayAmountInLots', newSettingsInLots);
    }
    static showAsync(ins) {
        return __awaiter(this, void 0, void 0, function* () {
            let x = null;
            let y;
            const oldScreen = instance;
            if (oldScreen != null) {
                x = oldScreen.getX();
                y = oldScreen.getY();
                oldScreen.close();
            }
            const needSetLocation = x !== null;
            instance = new SnapshotScreen(null, { instrument: ins, needCenterLocation: !needSetLocation });
            yield instance.getSnapshotBtnClickAsync();
            instance.on('teardown', function () {
                instance = null;
            });
            MainWindowManager.AddControl(instance);
            if (needSetLocation) {
                instance.setLocation(x, y);
            }
            instance.setFocus();
        });
    }
    sendSnapshotRequestMessageAsync() {
        return __awaiter(this, void 0, void 0, function* () {
            const instrument = this.instrument;
            if (isNullOrUndefined(instrument)) {
                return Promise.resolve(null);
            }
            const subsRoute = DataCache.Routes.getRouteById(instrument.Route);
            const route = subsRoute != null ? subsRoute.QuoteRouteId : null;
            if (!isNullOrUndefined(route)) {
                const result = yield DataCache.SendSnapshotRequestMessageAsync(instrument.InstrumentTradableID, route);
                if (!isNullOrUndefined(result)) {
                    return result;
                }
            }
            return Promise.resolve(null);
        });
    }
    getColoredTimeForHeader(dateObj) {
        const timeFormatted = DateTimeUtils.formatDate(dateObj, 'HH:mm:ss');
        return '<span class="snapshot-time">' + timeFormatted + '</span>';
    }
}
SnapshotScreen.VALUES_NAMES_LIST = [
    'Ask',
    'AskSize',
    'Bid',
    'BidSize',
    'Change',
    'ChangePercent',
    'High',
    'Last',
    'LastSize',
    'Low',
    'Volume'
];
TerceraWindowBase.extendWith(SnapshotScreen, {
    partials: { bodyPartial: SnapshotScreenTemplate },
    data: function () {
        return {
            name: 'snapshotScreen',
            movable: true,
            resizable: false,
            showFooter: false,
            focused: true,
            width: 368,
            height: 312,
            lastLabelText: 'Last',
            bidLabelText: 'Bid',
            askLabelText: 'Ask',
            priceLabelText: 'Price:',
            sizeLabelText: 'Size:',
            changeLabelText: 'Change:',
            highLabelText: 'Today high:',
            lowLabelText: 'Today low:',
            changePercentLabelText: 'Change, %:',
            volumeLabelText: 'Today volume:',
            strNA: StringUtils.THREE_DASHES,
            displayAmountInLots: null,
            getSnapshotBtnText: 'Get snapshot (99)',
            getSnapshotBtnTooltip: 'Snapshot requests limit has been reached',
            getSnapshotBtnDisabled: false,
            closeBtnText: 'Close'
        };
    },
    computed: {
        Ask: function () {
            if (this.message == null || this.instrument == null)
                return this.get('strNA');
            return InstrumentUtils.getFormatPrice(this.instrument, this.message.Ask, this.get('strNA'));
        },
        AskSize: function () {
            if (this.message == null || this.instrument == null)
                return this.get('strNA');
            return InstrumentUtils.getFormatVolume(this.instrument, this.message.AskSize, this.get('displayAmountInLots'), this.get('strNA'));
        },
        Bid: function () {
            if (this.message == null || this.instrument == null)
                return this.get('strNA');
            return InstrumentUtils.getFormatPrice(this.instrument, this.message.Bid, this.get('strNA'));
        },
        BidSize: function () {
            if (this.message == null || this.instrument == null)
                return this.get('strNA');
            return InstrumentUtils.getFormatVolume(this.instrument, this.message.BidSize, this.get('displayAmountInLots'), this.get('strNA'));
        },
        Change: function () {
            var _a;
            if (((_a = this.message) === null || _a === void 0 ? void 0 : _a.Last) == null || this.message.Close == null)
                return this.get('strNA');
            const last = this.message.Last;
            const close = this.message.Close;
            if (isNaN(last) || isNaN(close))
                return this.get('strNA');
            return InstrumentUtils.getFormatPrice(this.instrument, last - close, this.get('strNA'));
        },
        ChangePercent: function () {
            var _a;
            if (((_a = this.message) === null || _a === void 0 ? void 0 : _a.Last) == null || this.message.Close == null)
                return this.get('strNA');
            const last = this.message.Last;
            const close = this.message.Close;
            if (isNaN(last) || isNaN(close))
                return this.get('strNA');
            const value = ((last - close) / close) * 100;
            return InstrumentUtils.getFormatPrice(this.instrument, value, this.get('strNA'));
        },
        ChangeStyle: function () {
            var _a;
            if (((_a = this.message) === null || _a === void 0 ? void 0 : _a.Last) == null || this.message.Close == null)
                return '';
            const last = this.message.Last;
            const close = this.message.Close;
            if (isNaN(last) || isNaN(close) || MathUtils.equalToEpsilon(last, close))
                return '';
            const color = last > close ? 'positive' : 'negative';
            return color;
        },
        High: function () {
            if (this.message == null || this.instrument == null)
                return this.get('strNA');
            return InstrumentUtils.getFormatPrice(this.instrument, this.message.High, this.get('strNA'));
        },
        Last: function () {
            if (this.message == null || this.instrument == null)
                return this.get('strNA');
            return InstrumentUtils.getFormatPrice(this.instrument, this.message.Last, this.get('strNA'));
        },
        LastSize: function () {
            if (this.message == null || this.instrument == null)
                return this.get('strNA');
            return InstrumentUtils.getFormatVolume(this.instrument, this.message.LastSize, this.get('displayAmountInLots'), this.get('strNA'));
        },
        Low: function () {
            if (this.message == null || this.instrument == null)
                return this.get('strNA');
            return InstrumentUtils.getFormatPrice(this.instrument, this.message.Low, this.get('strNA'));
        },
        Volume: function () {
            if (this.message == null || this.instrument == null)
                return this.get('strNA');
            return InstrumentUtils.getFormatVolume(this.instrument, this.message.Volume, false, this.get('strNA'));
        }
    }
});
